import { useState } from 'react';
import { AlertTriangle, Info, Loader2, X } from 'lucide-react';
import { OUTBOUND_CALL_OUTCOME_LABELS, type CampaignSpec } from '@vagusx/shared';
import { HttpError } from '@vagusx/clients';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { LocationPicker } from '@/components/custom/location-picker';
import { useLocations } from '@/hooks/use-locations';
import { useDebounce } from '@/hooks/use-debounce';
import { useCampaignSimulation } from '@/hooks/use-outreach';

/**
 * The attempt calendar this campaign would produce for one patient.
 *
 * It runs the same `decideNextAction` and the same window resolver the driver
 * runs, so it cannot drift from what actually happens — which is what makes a
 * linear step editor sufficient instead of a node graph.
 *
 * Opened on demand rather than recomputed on every keystroke: the real flow is
 * edit → preview → publish, and the preview is a verification step.
 */

const REASON_LABELS: Record<string, string> = {
  exhausted: 'the sequence ran out',
  expired: 'the campaign gave up',
  max_attempts: 'the call cap was reached',
  error: 'the campaign cannot run',
};

function endedByLabel(reason: string): string {
  return (
    REASON_LABELS[reason] ??
    OUTBOUND_CALL_OUTCOME_LABELS.find((o) => o.value === reason)?.label.toLowerCase() ??
    reason
  );
}

interface PreviewColumnProps {
  spec: CampaignSpec;
  /** Absent: nothing to close (the Steps screen's sticky card). */
  onClose?: () => void;
  /** Fixed to one outcome, with no picker: "if they never pick up" (G). */
  fixedOutcome?: string;
  title?: string;
  /** `column` docks to the right edge; `card` sits in a layout of its own. */
  variant?: 'column' | 'card';
  /** False while the calendar is not on screen (a hidden editor tab): no simulation runs. */
  enabled?: boolean;
}

export function PreviewColumn({
  spec,
  onClose,
  fixedOutcome,
  title = 'Preview',
  variant = 'column',
  enabled = true,
}: PreviewColumnProps): React.ReactElement {
  const { data: locations } = useLocations();
  const [pickedOutcome, setOutcome] = useState('no_answer');
  const outcome = fixedOutcome ?? pickedOutcome;
  // A single-location organization has nothing to choose; the API resolves it.
  const [locationId, setLocationId] = useState<string | null>(null);

  // Editing with the column open re-simulates on every change; the API reads
  // the contact policy and the location timezone each time, so it is debounced
  // for the same reason the audience count is.
  const debouncedSpec = useDebounce(spec, 400);
  const simulation = useCampaignSimulation({
    spec: debouncedSpec,
    outcome,
    locationId,
    enabled,
  });

  const needsLocation =
    !locationId &&
    simulation.error instanceof HttpError &&
    simulation.error.hasFieldError('locationId');

  const formatter = new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: simulation.data?.timezone,
  });

  return (
    <div
      className={
        variant === 'column'
          ? 'flex h-full w-80 shrink-0 flex-col border-l'
          : 'flex flex-col rounded-lg border'
      }
    >
      <div className="flex items-center justify-between border-b px-4 py-3">
        <span className="text-sm font-medium">{title}</span>
        {onClose && (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={onClose}
            aria-label="Close preview"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      <div
        className={
          fixedOutcome && (locations?.items.length ?? 0) <= 1 ? 'hidden' : 'space-y-3 border-b p-4'
        }
      >
        <div className={fixedOutcome ? 'hidden' : 'space-y-1.5'}>
          <Label className="font-normal text-muted-foreground">Every call ends with</Label>
          <Select value={outcome} onValueChange={setOutcome}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {OUTBOUND_CALL_OUTCOME_LABELS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {(locations?.items.length ?? 0) > 1 && (
          <div className="space-y-1.5">
            <Label className="font-normal text-muted-foreground">Timezone from</Label>
            <LocationPicker
              selected={locationId ?? ''}
              onChange={(id) => setLocationId(id || null)}
              placeholder="Choose a location"
            />
          </div>
        )}
      </div>

      <div className={variant === 'column' ? 'flex-1 overflow-y-auto p-4' : 'p-4'}>
        {simulation.isFetching && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Working it out…
          </div>
        )}

        {!simulation.isFetching && simulation.isError && (
          <div className="flex items-start gap-2 text-sm text-destructive">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              {needsLocation
                ? 'Choose a location — the calendar would be wrong by hours without a timezone.'
                : simulation.error instanceof Error
                  ? simulation.error.message
                  : 'Could not preview this campaign.'}
            </span>
          </div>
        )}

        {!simulation.isFetching && simulation.data && (
          <>
            <ol className="space-y-2">
              {simulation.data.attempts.map((attempt, i) => (
                <li key={`${attempt.stepId}-${attempt.stepRun}-${i}`} className="text-sm">
                  <div className="tabular-nums">{formatter.format(new Date(attempt.at))}</div>
                  <div className="text-xs text-muted-foreground">
                    {attempt.stepLabel ?? attempt.stepId}
                    {attempt.stepRunsTotal > 1 &&
                      ` · call ${attempt.stepRun} of ${attempt.stepRunsTotal}`}
                    {attempt.leavesMessage && ' · leaves a message'}
                    {attempt.isCallback && ' · requested callback'}
                  </div>
                </li>
              ))}
            </ol>

            {simulation.data.attempts.length === 0 && (
              /*
                An empty calendar reads as "nothing scheduled yet" rather than
                "this can never run", so the reason is stated instead.
              */
              <p className="text-sm text-destructive">
                No call would ever be placed. Check the calling window — a window outside the
                organization's calling hours intersects to nothing.
              </p>
            )}

            {simulation.data.endedBy && (
              <p className="mt-3 border-t pt-3 text-sm text-muted-foreground">
                Ends after {simulation.data.attempts.length}{' '}
                {simulation.data.attempts.length === 1 ? 'call' : 'calls'} —{' '}
                {endedByLabel(simulation.data.endedBy.reason)}.
              </p>
            )}

            {simulation.data.caveats.length > 0 && (
              <div className="mt-3 space-y-1.5 border-t pt-3">
                {simulation.data.caveats.map((caveat) => (
                  <p
                    key={caveat}
                    className="flex items-start gap-1.5 text-xs text-muted-foreground"
                  >
                    <Info className="mt-0.5 h-3 w-3 shrink-0" />
                    <span>{caveat}</span>
                  </p>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
